import React, { useEffect } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Grid2,
    TextField,
    Toolbar,
    Typography
} from '@mui/material'
import CloudSyncIcon from '@mui/icons-material/CloudSync'
import DomainAddIcon from '@mui/icons-material/DomainAdd'
import VerifiedIcon from '@mui/icons-material/Verified'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import ModernNavbar from './ModernNavbar'
import Footer from './Footer'
import { getCompanyData, syncCompanyToMaster } from '../../features/setup/useableSlice'
import { canAccessCompanySync } from './menuAccess'

const CompanyPreviewLogo = ({ companyName, companyLogo, publicBaseUrl }) => {
    const [logoSrc, setLogoSrc] = React.useState(
        companyLogo && publicBaseUrl ? `${String(publicBaseUrl || '').replace(/\/$/, '')}/images/${companyLogo}` : ''
    )

    React.useEffect(() => {
        setLogoSrc(companyLogo && publicBaseUrl ? `${String(publicBaseUrl || '').replace(/\/$/, '')}/images/${companyLogo}` : '')
    }, [companyLogo, publicBaseUrl])

    if (!companyLogo || !logoSrc) {
        return (
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#16324F',
                    color: '#FFFFFF',
                    fontWeight: 800
                }}
            >
                {String(companyName || 'HR').slice(0, 2).toUpperCase()}
            </Box>
        )
    }

    return (
        <Box
            component="img"
            src={logoSrc}
            alt={companyName || 'Company Logo'}
            onError={() => {
                const fallbackSrc = `${String(publicBaseUrl || '').replace(/\/$/, '')}/uploads/images/${companyLogo}`
                if (logoSrc !== fallbackSrc) {
                    setLogoSrc(fallbackSrc)
                    return
                }

                setLogoSrc('')
            }}
            sx={{
                width: 52,
                height: 52,
                borderRadius: '16px',
                objectFit: 'contain',
                backgroundColor: '#fff'
            }}
        />
    )
}

const defaultTheme = {
    primaryColor: '#16324F',
    secondaryColor: '#0F766E',
    accentColor: '#F6BD60',
    backgroundColor: '#F4EFE6',
    surfaceColor: '#FFFDF8',
    textColor: '#16212F'
}

const hexToRgba = (hexColor, alpha) => {
    const normalized = String(hexColor || '').replace('#', '')

    if (normalized.length !== 6) {
        return `rgba(22, 50, 79, ${alpha})`
    }

    const red = parseInt(normalized.slice(0, 2), 16)
    const green = parseInt(normalized.slice(2, 4), 16)
    const blue = parseInt(normalized.slice(4, 6), 16)

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

const buildPreviewTheme = (theme) => ({
    ...theme,
    primaryTextColor: '#FFFFFF',
    secondaryTextColor: '#FFFFFF',
    mutedTextColor: hexToRgba(theme.textColor, 0.68),
    borderColor: hexToRgba(theme.primaryColor, 0.18),
    subtleSurfaceColor: hexToRgba(theme.primaryColor, 0.08),
    dangerColor: '#9C3D2A'
})

export default function CompanySync() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { user } = useSelector(state => state.auth)
    const { navbarMenu } = useSelector(state => state.menus)
    const { cmpData, isLoading, message, isError, isSuccess } = useSelector(state => state.ru)
    const [tenantCode, setTenantCode] = React.useState('')
    const [theme, setTheme] = React.useState(defaultTheme)
    const [publicBaseUrl, setPublicBaseUrl] = React.useState(String(global.SET_API_URL || '').replace(/\/$/, ''))

    useEffect(() => {
        if (!user) {
            navigate('/Login')
            return
        }

        if (navbarMenu.length > 0 && !canAccessCompanySync(navbarMenu)) {
            navigate('/Dashboard')
            return
        }

        dispatch(getCompanyData())
    }, [user, navigate, dispatch, navbarMenu])

    const company = cmpData && cmpData.length > 0 ? cmpData[0] : null
    const previewTheme = buildPreviewTheme(theme)
    const previewDate = new Date().toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    })
    const previewCards = [
        { key: 'present', label: 'Present Employees', value: 48 },
        { key: 'absent', label: 'Absent Employees', value: 5 },
        { key: 'late', label: 'Late Employees', value: 7 },
        { key: 'total', label: 'Total Employees', value: 53 }
    ]

    const handleSync = async () => {
        const resultAction = await dispatch(syncCompanyToMaster({
            tenantCode,
            theme,
            publicBaseUrl: String(publicBaseUrl || '').replace(/\/$/, '')
        }))

        if (syncCompanyToMaster.fulfilled.match(resultAction)) {
            alert('Company details were synced to the master backend successfully.')
            return
        }

        alert(resultAction.payload || 'Unable to sync company details to the master backend.')
    }

    const handleThemeChange = (key, value) => {
        setTheme((current) => ({
            ...current,
            [key]: value
        }))
    }

    return (
        <>
            <ModernNavbar />
            <Toolbar />
            <Container sx={{ py: 4 }}>
                <Grid2 container spacing={3}>
                    <Grid2 size={12}>
                        <Card sx={{ borderRadius: 4, background: 'linear-gradient(135deg, #16324f 0%, #1E90FF 100%)', color: 'white' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box display="flex" alignItems="center" gap={2} mb={2}>
                                    <DomainAddIcon sx={{ fontSize: 36 }} />
                                    <Typography variant="h4" fontWeight={800}>
                                        Company Master Sync
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: 760 }}>
                                    Register or update your current company in the master backend. The local backend will automatically sync the configured database connection details for this company.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 8 }}>
                        <Card sx={{ borderRadius: 4, height: '100%' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                    <Typography variant="h5" fontWeight={800}>
                                        Current Company
                                    </Typography>
                                    <Chip
                                        icon={<VerifiedIcon />}
                                        label={company ? 'Ready To Sync' : 'Company Not Found'}
                                        color={company ? 'success' : 'default'}
                                        variant={company ? 'filled' : 'outlined'}
                                    />
                                </Box>

                                {company ? (
                                    <Grid2 container spacing={2}>
                                        <Grid2 size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="caption" color="text.secondary">Company Name</Typography>
                                            <Typography variant="h6" fontWeight={700}>{company.cmpName || '-'}</Typography>
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="caption" color="text.secondary">Company ID</Typography>
                                            <Typography variant="h6" fontWeight={700}>{company.cmpID || '-'}</Typography>
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="caption" color="text.secondary">Email</Typography>
                                            <Typography variant="body1" fontWeight={600}>{company.cmpEmail || '-'}</Typography>
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="caption" color="text.secondary">Contact</Typography>
                                            <Typography variant="body1" fontWeight={600}>{company.cmpContact || '-'}</Typography>
                                        </Grid2>
                                        <Grid2 size={12}>
                                            <Typography variant="caption" color="text.secondary">Address</Typography>
                                            <Typography variant="body1" fontWeight={600}>{company.cmpAddress || '-'}</Typography>
                                        </Grid2>
                                        <Grid2 size={12}>
                                            <TextField
                                                fullWidth
                                                label="Company Code"
                                                placeholder="e.g. MALTEX"
                                                value={tenantCode}
                                                onChange={(e) => setTenantCode(e.target.value.toUpperCase())}
                                                helperText="Enter a unique company code that will be used for mobile sign-in and company mapping."
                                            />
                                        </Grid2>
                                        <Grid2 size={12}>
                                            <TextField
                                                fullWidth
                                                label="Public Backend URL"
                                                placeholder="e.g. http://192.168.100.57:5000"
                                                value={publicBaseUrl}
                                                onChange={(e) => setPublicBaseUrl(e.target.value)}
                                                helperText="Use the backend URL that is reachable from the mobile phone. Do not use localhost unless the phone and backend are on the same device."
                                            />
                                        </Grid2>
                                        <Grid2 size={12}>
                                            <Typography variant="h6" fontWeight={800} sx={{ mt: 1 }}>
                                                Mobile Theme
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Set the customer colors that should appear in the shared mobile app after sign-in.
                                            </Typography>
                                        </Grid2>
                                        {Object.entries(theme).map(([key, value]) => (
                                            <Grid2 key={key} size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    type="color"
                                                    label={key}
                                                    value={value}
                                                    onChange={(e) => handleThemeChange(key, e.target.value.toUpperCase())}
                                                    slotProps={{
                                                        inputLabel: {
                                                            shrink: true
                                                        }
                                                    }}
                                                    helperText={value}
                                                />
                                            </Grid2>
                                        ))}
                                    </Grid2>
                                ) : (
                                    <Alert severity="warning">No local company record was found yet. Create the company first, then sync it.</Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Card sx={{ borderRadius: 4 }}>
                            <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Typography variant="h5" fontWeight={800}>
                                    Sync Action
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    When you click this button, the current company and the local database connection details will be inserted or updated in the master backend.
                                </Typography>

                                {message ? (
                                    <Alert severity={isError ? 'error' : 'success'}>
                                        {message}
                                    </Alert>
                                ) : null}

                                {isSuccess && !isError ? (
                                    <Alert severity="success">
                                        The last sync completed successfully.
                                    </Alert>
                                ) : null}

                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<CloudSyncIcon />}
                                    onClick={handleSync}
                                    disabled={!company || isLoading}
                                    sx={{ py: 1.5, fontWeight: 800, borderRadius: 3, backgroundColor: theme.secondaryColor }}
                                >
                                    {isLoading ? 'Syncing...' : 'Sync Company'}
                                </Button>
                            </CardContent>
                        </Card>
                        <Card sx={{ borderRadius: 4, overflow: 'hidden' }}>
                            <CardContent sx={{ p: 0, backgroundColor: previewTheme.backgroundColor }}>
                                <Box sx={{ px: 3, pt: 3, pb: 2 }}>
                                    <Typography variant="h6" fontWeight={800} color="text.primary">
                                        Mobile App Preview
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Color changes will reflect here before you sync them to the mobile app.
                                    </Typography>
                                </Box>

                                <Box sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'center' }}>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            maxWidth: 340,
                                            borderRadius: '32px',
                                            border: '8px solid #111827',
                                            backgroundColor: previewTheme.backgroundColor,
                                            boxShadow: '0 24px 48px rgba(15, 23, 42, 0.22)',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 96,
                                                height: 20,
                                                borderBottomLeftRadius: 14,
                                                borderBottomRightRadius: 14,
                                                backgroundColor: '#111827',
                                                mx: 'auto',
                                                mb: 2
                                            }}
                                        />

                                        <Box sx={{ px: 2, pb: 3 }}>
                                            <Box
                                                sx={{
                                                    borderRadius: '26px',
                                                    px: 2.5,
                                                    py: 2,
                                                    mb: 2,
                                                    backgroundColor: previewTheme.surfaceColor,
                                                    border: `1px solid ${previewTheme.borderColor}`
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 20, fontWeight: 800, color: previewTheme.textColor, mb: 0.5 }}>
                                                    Welcome, {user?.userName || 'Admin User'}
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, color: previewTheme.mutedTextColor, mb: 2 }}>
                                                    You are connected to {company?.cmpName || 'Your Company'}.
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        borderRadius: '20px',
                                                        border: `1px solid ${previewTheme.borderColor}`,
                                                        backgroundColor: previewTheme.subtleSurfaceColor,
                                                        px: 1.5,
                                                        py: 1.25,
                                                        mb: 2
                                                    }}
                                                >
                                                    {company?.cmpImage ? (
                                                        <CompanyPreviewLogo
                                                            companyName={company?.cmpName}
                                                            companyLogo={company?.cmpImage}
                                                            publicBaseUrl={publicBaseUrl}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                width: 52,
                                                                height: 52,
                                                                borderRadius: '16px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                backgroundColor: previewTheme.primaryColor,
                                                                color: previewTheme.primaryTextColor,
                                                                fontWeight: 800
                                                            }}
                                                        >
                                                            {String(company?.cmpName || 'HR').slice(0, 2).toUpperCase()}
                                                        </Box>
                                                    )}

                                                    <Box>
                                                        <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: previewTheme.mutedTextColor, mb: 0.3 }}>
                                                            Tenant Company
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: previewTheme.textColor }}>
                                                            {company?.cmpName || 'Your Company'}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        borderRadius: '18px',
                                                        border: `1px solid ${previewTheme.borderColor}`,
                                                        backgroundColor: previewTheme.subtleSurfaceColor,
                                                        px: 2,
                                                        py: 1.5,
                                                        textAlign: 'center',
                                                        mb: 2
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: previewTheme.mutedTextColor }}>
                                                        Selected Date
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: previewTheme.textColor }}>
                                                        {previewDate}
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        borderRadius: '22px',
                                                        border: `1px solid ${previewTheme.borderColor}`,
                                                        backgroundColor: previewTheme.subtleSurfaceColor,
                                                        p: 2,
                                                        mb: 2
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 16, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, color: previewTheme.textColor, mb: 1.5 }}>
                                                        Attendance
                                                    </Typography>

                                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
                                                        {previewCards.map((card) => (
                                                            <Box
                                                                key={card.key}
                                                                sx={{
                                                                    borderRadius: '12px',
                                                                    px: 1.5,
                                                                    py: 1.25,
                                                                    textAlign: 'center',
                                                                    backgroundColor: previewTheme.primaryColor
                                                                }}
                                                            >
                                                                <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: previewTheme.accentColor, mb: 0.75 }}>
                                                                    {card.label}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 28, lineHeight: 1.1, fontWeight: 800, color: previewTheme.primaryTextColor }}>
                                                                    {card.value}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>

                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    sx={{
                                                        mb: 1,
                                                        py: 1.4,
                                                        borderRadius: '18px',
                                                        fontWeight: 800,
                                                        backgroundColor: previewTheme.secondaryColor,
                                                        color: previewTheme.secondaryTextColor
                                                    }}
                                                >
                                                    Sign Out
                                                </Button>
                                                <Typography sx={{ textAlign: 'center', fontSize: 13, fontWeight: 700, color: previewTheme.dangerColor, py: 1 }}>
                                                    Ready for next step
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                        </Box>
                    </Grid2>
                </Grid2>
            </Container>
            <Footer />
        </>
    )
}